import { Box, Button, Grid, Stack, Text, Title } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { z } from 'zod';

import { NCTSMessageType, Transit, TransitStatus } from '@e-origin/shared';

import { DatePickerInput, LeavePageNotification, TextInput } from '@components';
import { NON_ARRIVAL_COUNTRY_CODE_LENGTH, NON_ARRIVAL_REFERENCE_NUMBER_LENGTH } from '@constants';
import { useSendNonArrivalInfo } from '@hooks/transit';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';

const nonArrivalSchema = z.object({
  CustomsOfficeOfDestinationActualReferenceNumber: z.string().length(NON_ARRIVAL_REFERENCE_NUMBER_LENGTH, 'Reference number must be exactly 8 characters'),
  TC11DeliveryDate: z.date().optional(),
  EnquiryText: z.string().optional(),
  identificationNumber: z.string().optional(),
  name: z.string().optional(),
  street: z.string().optional(),
  postcode: z.string().optional(),
  city: z.string().optional(),
  country: z.string().length(NON_ARRIVAL_COUNTRY_CODE_LENGTH, 'Country must be exactly 2 characters').or(z.string().max(0)),
});

type NonArrivalFormValues = z.infer<typeof nonArrivalSchema>;

interface NonArrivalInfoProps {
  transit: Transit;
}

export const NonArrivalInfo: React.FC<NonArrivalInfoProps> = ({ transit }) => {
  const sendNonArrivalInfoMutation = useSendNonArrivalInfo();
  const isDisabled = transit.generalInfo.status !== TransitStatus.REQUESTED_NON_ARRIVED;

  const buildInitialValues = (): NonArrivalFormValues => ({
    CustomsOfficeOfDestinationActualReferenceNumber: transit?.CustomsOfficeOfDestinationActual?.referenceNumber || '',
    TC11DeliveryDate: transit?.enquiry?.TC11DeliveryDate ? new Date(transit.enquiry.TC11DeliveryDate) : null,
    EnquiryText: transit?.enquiry?.text || '',
    identificationNumber: transit?.Consignment?.ConsigneeActual?.identificationNumber || '',
    name: transit?.Consignment?.ConsigneeActual?.name || '',
    street: transit?.Consignment?.ConsigneeActual?.Address?.streetAndNumber || '',
    postcode: transit?.Consignment?.ConsigneeActual?.Address?.postcode || '',
    city: transit?.Consignment?.ConsigneeActual?.Address?.city || '',
    country: transit?.Consignment?.ConsigneeActual?.Address?.country || '',
  });

  const form = useFormWithNameInputProp<NonArrivalFormValues>({
    initialValues: buildInitialValues(),
    validate: zodResolver(nonArrivalSchema),
    validateInputOnBlur: true,
  });

  const handleSubmit = (values: NonArrivalFormValues) => {
    sendNonArrivalInfoMutation.mutate(
      {
        id: transit._id,
        data: {
          CustomsOfficeOfDestinationActualReferenceNumber: values.CustomsOfficeOfDestinationActualReferenceNumber,
          TC11DeliveryDate: values.TC11DeliveryDate
            ? new Date(Date.UTC(values.TC11DeliveryDate.getFullYear(), values.TC11DeliveryDate.getMonth(), values.TC11DeliveryDate.getDate()))
            : null,
          EnquiryText: values.EnquiryText,
          ConsigneeActual: {
            identificationNumber: values.identificationNumber,
            name: values.name,
            Address: {
              streetAndNumber: values.street,
              postcode: values.postcode,
              city: values.city,
              country: values.country,
            },
          },
        },
      },
      {
        onSettled: (data, error, variables, context) => {
          if (!error) {
            form.resetDirty();
          }
        },
      },
    );
  };

  const errors = transit.customsState?.functionalError?.filter((error) =>
    error.errorPointer.toLocaleLowerCase().includes(NCTSMessageType.CC141C.toLocaleLowerCase()),
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <Stack gap="xs" mb="md">
        <Title order={5} fw={600}>
          Request for non-arrived movement
        </Title>

        {errors?.length > 0 && (
          <Box>
            {errors.map((error, idx) => (
              <Text key={idx} c="red">
                Error pointer: {error.errorPointer} (Error code: {error.errorCode}) {error.originalAttributeValue}
              </Text>
            ))}
          </Box>
        )}

        <Grid>
          <Grid.Col span={4}>
            <DatePickerInput label="Request date" value={new Date(transit.customsState?.requestNonArrived?.requestDate)} disabled={true} />
          </Grid.Col>
          <Grid.Col span={4}>
            <DatePickerInput label="Response deadline" value={new Date(transit.customsState?.requestNonArrived?.limitForResponseDate)} disabled={true} />
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput
              label="Office of enquiry"
              value={transit.customsState?.requestNonArrived?.customsOfficeOfEnquiryAtDeparture?.referenceNumber}
              disabled={true}
            />
          </Grid.Col>
        </Grid>

        <Title order={5} fw={600}>
          Customs office of destination (Actual)
        </Title>

        <Grid>
          <Grid.Col span={4}>
            <TextInput
              {...form.getInputProps('CustomsOfficeOfDestinationActualReferenceNumber')}
              label="Reference number"
              disabled={isDisabled}
              data-pw="reference-number-input"
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <DatePickerInput {...form.getInputProps('TC11DeliveryDate')} label="TC11 Delivery Date" disabled={isDisabled} data-pw="tc11-date-input" />
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput {...form.getInputProps('EnquiryText')} label="Text" disabled={isDisabled} data-pw="enquiry-text-input" />
          </Grid.Col>
        </Grid>

        <Title order={5} fw={600}>
          Consignee (Actual)
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <TextInput
              {...form.getInputProps('identificationNumber')}
              label="Identification number"
              disabled={isDisabled}
              data-pw="identification-number-input"
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput {...form.getInputProps('name')} label="Name" disabled={isDisabled} data-pw="name-input" />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('street')} label="Street" disabled={isDisabled} data-pw="street-input" />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('postcode')} label="Postcode" disabled={isDisabled} data-pw="postcode-input" />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('city')} label="City" disabled={isDisabled} data-pw="city-input" />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps('country')} label="Country" disabled={isDisabled} data-pw="country-input" />
          </Grid.Col>
        </Grid>

        {!isDisabled && (
          <Button
            type="submit"
            disabled={!form.isValid()}
            variant="filled"
            size="lg"
            mt="xl"
            loading={sendNonArrivalInfoMutation.isPending}
            data-pw="send-button"
          >
            Send response
          </Button>
        )}
      </Stack>
    </form>
  );
};
